import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, GridLegacy, IconButton, TableCell, TextField, Tooltip } from "@mui/material";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import MainNavbar from "../../components/Navbars/MainNavbar";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Button from "../../components/CustomButtons/Button";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import { withStyles } from "tss-react/mui";
import vestigingStyle from "../../assets/jss/material-dashboard-pro-react/views/vestigingStyle";
import { Add, Delete } from "@mui/icons-material";
import CreateNewPermissionDialog from "./CreateNewPermissionDialog";
import JamezzTableV2, { useReloadState } from "../../components/JamezzTable/V2/JamezzTableV2";
import FormElement from "../../components/JamezzTable/FormElement";
import Person from "@mui/icons-material/Person";
import { Link } from "react-router";
import Edit from "@mui/icons-material/Edit";
import AreYouSureDialog from "../../components/Dialogs/AreYouSureDialog";
import { _post } from "../../http";
import { useDialog } from "../../licensing/utils/DialogProvider";
import useJTFilter from "../../components/JamezzTable/V2/useJTFilter";
import UploadPermissionsDialog from "./UploadPermissionsDialog";

const endPoint = "core/permissions";

function Permissions({ classes }) {
  const { reloadState, updateReloadState } = useReloadState();
  const [openDialog] = useDialog();
  const [showPermissionsOnlyWithNoRoles, setShowPermissionsOnlyWithNoRoles] = useState(false);

  const [textField, filters] = useJTFilter("name");
  const [filters1, setFilters1] = useState(filters);
  const [newPermissionDialogIsOpen, setNewPermissionDialogIsOpen] = useState(false);

  useEffect(() => {
    const filters1 = { ...filters };
    if (showPermissionsOnlyWithNoRoles) {
      filters1["noroles"] = { field: "roles.id", has: false };
    }
    setFilters1(filters1);
  }, [filters, showPermissionsOnlyWithNoRoles]);

  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Permission name"} tKey={"permissions.name"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
      {
        id: "created_at",
        label: <TranslationWrapper tKey={"Created at"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
    ],
    []
  );

  const renderRowCells = useCallback((row, isEditable) => {
    return (
      <>
        <TableCell align={"right"} style={{ width: 100 }}>
          {row.id}
        </TableCell>
        <TableCell>
          {isEditable ? (
            <FormElement endPoint={"core/permissions"} name={"name"} row={row}>
              <TextField size={"small"} variant={"outlined"} />
            </FormElement>
          ) : null}
          {!isEditable ? row.name : null}
        </TableCell>
        <TableCell>{row.created_at}</TableCell>
      </>
    );
  }, []);

  const renderSelectionActions = useCallback((selected) => {
    return (
      <Tooltip title={<TranslationWrapper tKey={"Delete"} />}>
        <IconButton
          onClick={() => {
            openDialog({
              children: (
                <AreYouSureDialog
                  content={<TranslationWrapper tKey={"Are you sure you want to delete the selected permissions?"} />}
                  onOk={() => {
                    _post("/core/permissions/multipledelete", { ids: selected }).then(() => {
                      updateReloadState();
                    });
                  }}
                />
              ),
            });
          }}
          style={{ color: "lightcoral" }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    );
  }, []);

  const renderRowActions = useCallback((row) => {
    return (
      <TableCell align={"right"}>
        <Link to={`./${row.id}/edit`}>
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            placement={"bottom"}
            title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
          >
            <IconButton color={"primary"} simple size={"small"}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Link>
      </TableCell>
    );
  }, []);

  return (
    <div>
      <MainNavbar paginaTitel={<TranslationWrapper tDefault={"Permissions"} tKey={"permissions.table.title"} />} />
      <GridContainer>
        <GridItem xs={12}>
          <AuthenticationWrapper
            child={
              <Button
                className={classes.marginRight}
                color={"success"}
                onClick={() => {
                  setNewPermissionDialogIsOpen(true);
                }}
              >
                <Add className={classes.icons} />{" "}
                <TranslationWrapper tDefault={"Create new permission"} tKey={"permissions.createNewPermission"} />
              </Button>
            }
            permissionKey={"permissions.create"}
          />
          <AuthenticationWrapper
            child={(write) =>
              write ? (
                <Link target={"_blank"} to={"core/permissions/export"}>
                  <Button className={classes.marginRight} color={"success"}>
                    <TranslationWrapper
                      tDefault={"Download permissions configuration"}
                      tKey={"permissions.downloadAllPermissions"}
                    />
                  </Button>
                </Link>
              ) : null
            }
            permissionKey={"permissions.exportAllPermissions"}
          />
          <AuthenticationWrapper
            child={(write) =>
              write ? (
                <Button
                  className={classes.marginRight}
                  color={"success"}
                  onClick={() => {
                    openDialog({ children: <UploadPermissionsDialog /> });
                  }}
                >
                  <TranslationWrapper
                    tDefault={"Upload permissions configuration"}
                    tKey={"permissions.importAllPermissions"}
                  />
                </Button>
              ) : null
            }
            permissionKey={"permissions.importAllPermissions"}
          />
        </GridItem>
      </GridContainer>
      <GridLegacy container spacing={1}>
        <GridLegacy container item xs={12}>
          <GridLegacy item xs={12}>
            {textField}

            <FormControlLabel
              control={
                <Checkbox
                  checked={showPermissionsOnlyWithNoRoles}
                  color={"primary"}
                  onChange={(e) => {
                    setShowPermissionsOnlyWithNoRoles(e.target.checked);
                  }}
                />
              }
              label={<TranslationWrapper tKey={"Only show permissions with no roles."} />}
            />
          </GridLegacy>
          <JamezzTableV2
            checkboxSelection={true}
            dense={true}
            endPoint={"core/permissions"}
            filters={filters1}
            headerIcon={<Person />}
            reloadState={reloadState}
            renderCellsForRow={renderRowCells}
            renderHeaderCells={renderHeaderCells}
            renderRowActions={renderRowActions}
            renderSelectionActions={renderSelectionActions}
            rowIsEditable={() => false}
            tableTitle={<TranslationWrapper tDefault={"Permissions"} tKey={"permissions.table.title"} />}
            uuid={"Permissions-osjd019jweio"}
          />
        </GridLegacy>
      </GridLegacy>
      {newPermissionDialogIsOpen ? (
        <CreateNewPermissionDialog
          onClose={() => {
            setNewPermissionDialogIsOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

export default withStyles(Permissions, vestigingStyle);
